import { useCallback, useMemo, useState } from 'react';
import {
  addToDate,
  computeSubscriptionPreview,
  getDefaultPlanForBusiness,
  isContactSalesPlan,
  isUsagePlan,
  minUsagePurchaseQty,
  resolveUsagePlanUpgrade,
  SUBSCRIPTION_CONFIG,
  type SubscriptionInterval,
  type SubscriptionPreview,
} from '@hero/core';
import type { BillingUnit, Business, Currency, Id, SubscriptionPublic } from '@hero/schema';
import { usePlanPickerManager } from './usePlanPickerManager';
import { useSubscriptionAudience } from './useSubscriptionAudience';
import { useSubscriptionPayManager } from './useSubscriptionPayManager';

/**
 * Drives the post-redesign single-screen checkout (plan picker + sticky
 * order summary + redirect overlay).
 *
 * Replaces the older two-step `plan → summary` wizard. Owns:
 *
 *   - plan / interval / committed-seats selection (via `usePlanPickerManager`)
 *   - the `SubscriptionPreview` math (via `computeSubscriptionPreview`)
 *   - redirect-overlay open state (mounted from the moment we call
 *     `pay.mutateAsync` until the browser either lands back on
 *     `/billing` or navigates to Moyasar's hosted page — gives the
 *     user visible "we're working on it" feedback so the page never
 *     freezes onto a blank screen)
 *   - the `submit()` action
 *
 * Variant rules (drives summary CTA + behavior):
 *
 *   - `custom` → no totals, no pay flow; page handles `Contact sales`.
 *   - `free`   → no charge; `submit()` field-updates and navigates back.
 *   - `pay`    → every paid scenario (new signup, trial conversion,
 *                mid-period upgrade, mid-period seat increase).
 *                `submit()` routes directly through
 *                `useSubscriptionPayManager.pay`, which either silently
 *                charges the saved token, redirects to Moyasar's hosted
 *                page, or field-updates depending on what the backend
 *                returns. There is intentionally NO interstitial confirm
 *                dialog — the order summary already shows the exact
 *                total, plan, fleet size, and renewal date, and Moyasar
 *                renders its own confirm step on the hosted page, so a
 *                "Confirm payment" modal in between is pure friction.
 */

export type BillingCheckoutVariant = 'pay' | 'free' | 'custom';

export interface UseBillingCheckoutManagerOptions {
  orgId?: Id;
  subscription: SubscriptionPublic | null;
  /** Which brand's ladder to sell. Defaults to the Hero dashboard. */
  business?: Business;
  /**
   * Which way of paying to open on when there's no live subscription.
   *
   * Callers derive this from what the org actually does — see
   * {@link resolveDefaultBillingUnit}. It used to be hardcoded to the
   * seat ladder, which meant an org that ships forty orders a month and
   * owns no trucks landed on a card quoting it fifteen seats.
   */
  defaultUnit?: BillingUnit;
  /**
   * Plan to pre-select when there's no live subscription. Defaults to
   * the recommended tier on `defaultUnit`, from the catalog.
   */
  defaultPlanCode?: string;
  /**
   * Default committed-fleet size used when there's no live subscription.
   * Falls back to `SUBSCRIPTION_CONFIG.committedVehicleQty.default`.
   */
  defaultCommittedVehicleQty?: number;
  /**
   * How many order credits the picker opens on when there's no live
   * subscription on an `order` plan. Falls back to
   * `SUBSCRIPTION_CONFIG.usage.defaultQty`.
   */
  defaultCommittedOrderQty?: number;
  /** Default currency used when there's no live subscription. */
  defaultCurrency?: Currency;
}

export function useBillingCheckoutManager(options: UseBillingCheckoutManagerOptions) {
  const {
    orgId,
    subscription,
    business = 'hero',
    defaultUnit = 'vehicle',
    defaultPlanCode = getDefaultPlanForBusiness(business, defaultUnit),
    defaultCommittedVehicleQty = SUBSCRIPTION_CONFIG.committedVehicleQty.default,
    defaultCommittedOrderQty = SUBSCRIPTION_CONFIG.usage.defaultQty,
    defaultCurrency = 'SAR',
  } = options;

  // Trial / legacy subscriptions can sit below the paid-plan minimum
  // (e.g. `committedVehicleQty: 1` on a trial). Clamp the seed so the
  // picker always lands on a valid slider stop instead of leaving the
  // big number out-of-sync with the thumb position.
  const minCommittedVehicleQty = SUBSCRIPTION_CONFIG.committedVehicleQty.min;
  const seededCommittedVehicleQty = Math.max(
    subscription?.committedVehicleQty ?? defaultCommittedVehicleQty,
    minCommittedVehicleQty,
  );

  // An owner-operator buys from a different ladder than a company, so
  // the audience has to reach every price on this screen — the seeded
  // quantity included, since the two ladders can open at different
  // floors.
  const audience = useSubscriptionAudience(subscription);

  // Not seeded from the subscription: what they bought last time is
  // spent, and pre-filling the box with it quietly proposes the same
  // purchase again. The default is the ladder's own opening size.
  const seededCommittedOrderQty = Math.max(
    defaultCommittedOrderQty,
    minUsagePurchaseQty(business, audience),
  );

  // Interval seeding policy:
  //  - No subscription -> default to `year` to nudge new buyers toward
  //    the annual plan (better LTV + the bigger advertised discount).
  //  - Trialing subscription -> treat as "uncommitted" and also seed
  //    to `year`. The trial interval (`month`) is a placeholder, not
  //    a real commitment, so respecting it here would dampen the
  //    yearly nudge during the most important conversion moment.
  //  - Active / past_due / paused / canceled paid sub -> respect
  //    `subscription.interval`. These users have an existing financial
  //    relationship on a specific cycle; silently switching them to
  //    yearly when they open the picker would feel like the UI is
  //    changing their plan behind their back.
  //  - Prepaid sub -> has no interval at all, so there is nothing to
  //    respect and the nudge applies as if they were new.
  const seededInterval: SubscriptionInterval =
    (subscription && subscription.status !== 'trialing' ? subscription.interval : undefined) ??
    'year';

  const livePlanCode = subscription?.purchase?.planCode;

  const planPicker = usePlanPickerManager({
    initialPlanCode: livePlanCode ?? defaultPlanCode,
    initialUnit: defaultUnit,
    business,
    audience,
    initialInterval: seededInterval,
    initialCommittedVehicleQty: seededCommittedVehicleQty,
    initialCommittedOrderQty: seededCommittedOrderQty,
    initialCurrency: subscription?.currency ?? defaultCurrency,
  });

  const payManager = useSubscriptionPayManager({ subscription, orgId });

  const [isRedirecting, setIsRedirecting] = useState(false);

  /**
   * The plan that will actually be bought.
   *
   * On the seat ladder this is whatever card the user clicked. On the
   * credit ladder it is not theirs to click: the tier comes with the
   * volume, and a plan the picker sent that the quantity did not earn
   * would be a screen promising features the backend then declines to
   * grant. `resolveUsagePlanUpgrade` also keeps a tier they already
   * hold, so a small top-up cannot demote them.
   */
  const resolvedPlanCode = useMemo(() => {
    if (!planPicker.planCode) return undefined;
    if (!isUsagePlan(planPicker.planCode)) return planPicker.planCode;
    return resolveUsagePlanUpgrade({
      business,
      audience,
      qty: planPicker.committedOrderQty,
      currentPlanCode: livePlanCode,
    });
  }, [
    planPicker.planCode,
    planPicker.committedOrderQty,
    business,
    audience,
    livePlanCode,
  ]);

  const preview = useMemo<SubscriptionPreview | null>(() => {
    if (!resolvedPlanCode) return null;
    // Both quantities go in; the preview reads whichever the plan's
    // unit selects and ignores the other.
    return computeSubscriptionPreview({
      planCode: resolvedPlanCode,
      interval: planPicker.interval,
      committedVehicleQty: planPicker.committedVehicleQty,
      committedOrderQty: planPicker.committedOrderQty,
      currency: planPicker.currency,
      business,
      audience,
    });
  }, [
    resolvedPlanCode,
    planPicker.interval,
    planPicker.committedVehicleQty,
    planPicker.committedOrderQty,
    planPicker.currency,
    business,
    audience,
  ]);

  const variant: BillingCheckoutVariant = useMemo(() => {
    if (!planPicker.planCode) return 'pay';
    if (isContactSalesPlan(planPicker.planCode)) return 'custom';
    if (planPicker.planCode === 'free') return 'free';
    return 'pay';
  }, [planPicker.planCode]);

  /**
   * When the next renewal will happen.
   *
   * - **Pay variant + active paid sub** — use the live subscription's
   *   `currentPeriodEnd`. Mid-period upgrades preserve the existing
   *   window (proration covers the remainder), so the next charge is
   *   still at that date.
   * - **Pay variant + no/trialing sub** — estimate `now + 1 interval`
   *   using the shared `addToDate` util (calendar-aware: 1 month from
   *   Jan 31 → Feb 28/29, not Feb 30). Backend replaces it with the
   *   real `currentPeriodEnd` on the very first refetch after the
   *   Moyasar redirect lands.
   * - **Free / custom** — no renewal date (free is no charge, custom is
   *   negotiated externally).
   */
  const nextRenewalAt: Date | undefined = useMemo(() => {
    if (variant === 'free' || variant === 'custom') return undefined;
    // Credits do not renew and do not expire, so there is no date to
    // show. Showing one would be inventing a deadline.
    if (resolvedPlanCode && isUsagePlan(resolvedPlanCode)) return undefined;
    if (subscription && subscription.status !== 'trialing') {
      return subscription.currentPeriodEnd;
    }
    return estimateNextRenewal(planPicker.interval);
  }, [variant, subscription, planPicker.interval, resolvedPlanCode]);

  /**
   * Primary-CTA handler. Mounts the redirect overlay and routes
   * straight through the unified `useSubscriptionPayManager.pay`. The
   * manager itself decides whether to silently charge the saved token,
   * hand off to Moyasar's hosted page (`window.location.assign`), or
   * just navigate back to the overview after a field-only change. The
   * overlay stays mounted for the duration of either branch; if the
   * call rejects we tear it down so the user can retry.
   *
   * No interstitial confirm dialog — Moyasar's hosted page already
   * shows its own card-entry / "Pay" step, and the order summary on
   * this page already shows the exact total + renewal date, so a
   * modal in between is friction without information value.
   */
  const resolvedUnit = planPicker.planCode ? planPicker.unit : defaultUnit;

  const submit = useCallback(async () => {
    if (!resolvedPlanCode) return;
    if (variant === 'custom') return;
    setIsRedirecting(true);
    try {
      await payManager.pay({
        planCode: resolvedPlanCode,
        interval: planPicker.interval,
        committedVehicleQty: planPicker.committedVehicleQty,
        committedOrderQty: planPicker.committedOrderQty,
      });
    } catch (err) {
      setIsRedirecting(false);
      throw err;
    }
  }, [
    resolvedPlanCode,
    planPicker.interval,
    planPicker.committedVehicleQty,
    planPicker.committedOrderQty,
    variant,
    payManager,
  ]);

  return {
    planPicker,
    preview,
    variant,
    business,
    /**
     * Which ladder the screen is quoting from. Passed down so the plan
     * cards price on the same axis the summary totals do — a card
     * showing company rates above a freelancer's total is the kind of
     * mismatch that gets read as a bug in the total.
     */
    audience,
    /** The plan the purchase will land on — see {@link resolvedPlanCode}. */
    resolvedPlanCode,
    /** Which unit tab the screen should open on. */
    defaultUnit: resolvedUnit,
    nextRenewalAt,
    isRedirecting,
    isSubmitting: payManager.isSubmitting,
    submit,
  };
}

/**
 * Which way of paying to lead with, from what the org actually does.
 *
 * A seat plan only makes sense once the fleet is big enough to justify
 * the commitment floor — below it, the checkout is quoting seats the
 * buyer does not have. An org that has been creating orders is telling
 * us plainly which meter fits, so that signal wins when present.
 *
 * A live subscription overrides all of this upstream: the manager seeds
 * from `subscription.purchase.planCode`, and this is only consulted when
 * there is nothing to seed from.
 */
export function resolveDefaultBillingUnit(input: {
  /** Vehicles the org has registered, if known. */
  vehicleCount?: number;
  /** Orders the org created in the current period, if known. */
  ordersThisPeriod?: number;
}): BillingUnit {
  const { vehicleCount, ordersThisPeriod } = input;
  const fleetTooSmallToCommit =
    vehicleCount !== undefined &&
    vehicleCount < SUBSCRIPTION_CONFIG.committedVehicleQty.min;
  if (fleetTooSmallToCommit) return 'order';
  if (vehicleCount === undefined && (ordersThisPeriod ?? 0) > 0) return 'order';
  return 'vehicle';
}

/** @deprecated Prefer {@link resolveDefaultBillingUnit}. */
export const resolveDefaultBillingBasis = resolveDefaultBillingUnit;

/**
 * Calendar-aware estimate for "today + 1 interval". Uses `addToDate`
 * so month/year math respects calendar boundaries (no 30-day rolling
 * approximations).
 */
function estimateNextRenewal(interval: SubscriptionInterval): Date {
  switch (interval) {
    case 'month':
      return addToDate(1, 'month').date;
    case 'semester':
      return addToDate(6, 'month').date;
    case 'year':
      return addToDate(1, 'year').date;
  }
}
